import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { usePermissions } from '../contexts/PermissionContext';
import apiService from '../services/apiService';
import { 
  FileSpreadsheet, 
  Download, 
  Upload, 
  Package, 
  Users, 
  Truck,
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  RefreshCw,
  FileText,
  Database,
  Shield,
  Zap,
  Eye,
  EyeOff,
  BarChart3,
  TrendingUp,
  Activity,
  Clock,
  FileCheck,
  FileX,
  X,
  Settings,
  Info,
  ArrowRight,
  Filter,
  Search,
  Calendar,
  HardDrive,
  Cloud,
  Archive,
  Layers,
  Grid3X3,
  List,
  Star,
  Bookmark,
  Tag,
  Folder,
  FolderOpen,
  File,
  FileCode,
  FileImage,
  FileVideo,
  FileAudio,
  FileArchive,
  FileMinus,
  FilePlus,
  FileEdit,
  FileSearch,
  FileSliders,
  FileBarChart,
  FilePieChart,
  FileLineChart,
  FileHeart,
  FileLock,
  FileKey,
  FileUser,
  FileCog,
  UserCheck,
  CreditCard,
  DollarSign,
  Receipt,
  ShoppingCart,
  Plus,
  MoreVertical,
  Target,
  Timer
} from 'lucide-react';
import toast from 'react-hot-toast';

const DataManagement = () => {
  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(false);
  const [exportData, setExportData] = useState({
    products: { count: 0, lastExport: null, size: 0 },
    customers: { count: 0, lastExport: null, size: 0 },
    suppliers: { count: 0, lastExport: null, size: 0 }
  });
  const [importHistory, setImportHistory] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importSettings, setImportSettings] = useState({
    updateExisting: false,
    skipDuplicates: true,
    validateData: true
  });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // grid or list

  // Check permissions
  const canExport = hasPermission('system.export');
  const canImport = hasPermission('system.import');
  const canView = hasPermission('system.view');

  useEffect(() => {
    if (canExport || canImport) {
      fetchExportData();
      fetchImportHistory();
    }
  }, [canExport, canImport]);

  const fetchExportData = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.get('/system/export-data');
      if (response.success && response.data) {
        // Ensure all required fields exist with proper defaults
        const data = {
          products: {
            count: Number(response.data.products?.count) || 0,
            size: Number(response.data.products?.size) || 0,
            lastExport: response.data.products?.lastExport || null
          },
          customers: {
            count: Number(response.data.customers?.count) || 0,
            size: Number(response.data.customers?.size) || 0,
            lastExport: response.data.customers?.lastExport || null
          },
          suppliers: {
            count: Number(response.data.suppliers?.count) || 0,
            size: Number(response.data.suppliers?.size) || 0,
            lastExport: response.data.suppliers?.lastExport || null
          }
        };
        
        
        setExportData(data);
      } else {
        // Fallback to mock data
        setExportData({
          products: { count: 1250, lastExport: '2024-01-15 10:30:00', size: 2.5 },
          customers: { count: 340, lastExport: '2024-01-14 15:45:00', size: 0.8 },
          suppliers: { count: 85, lastExport: '2024-01-13 09:20:00', size: 0.3 }
        });
      }
    } catch (error) {
      console.error('Error fetching export data:', error);
      // Use mock data as fallback
      setExportData({
        products: { count: 1250, lastExport: '2024-01-15 10:30:00', size: 2.5 },
        customers: { count: 340, lastExport: '2024-01-14 15:45:00', size: 0.8 },
        suppliers: { count: 85, lastExport: '2024-01-13 09:20:00', size: 0.3 }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchImportHistory = async () => {
    try {
      const response = await apiService.get('/system/import-history');
      if (response.success) {
        setImportHistory(response.data);
      } else {
        // Fallback to mock data
        setImportHistory([
          {
            id: 1,
            type: 'products',
            filename: 'products_import_2024_01_15.xlsx',
            records: 150,
            success: 148,
            failed: 2,
            created_at: '2024-01-15 14:30:00',
            status: 'completed'
          },
          {
            id: 2,
            type: 'customers',
            filename: 'customers_import_2024_01_14.xlsx',
            records: 50,
            success: 50,
            failed: 0,
            created_at: '2024-01-14 11:15:00',
            status: 'completed'
          },
          {
            id: 3,
            type: 'suppliers',
            filename: 'suppliers_import_2024_01_13.xlsx',
            records: 25,
            success: 23,
            failed: 2,
            created_at: '2024-01-13 16:45:00',
            status: 'completed'
          }
        ]);
      }
    } catch (error) {
      console.error('Error fetching import history:', error);
      // Use mock data as fallback
      setImportHistory([
        {
          id: 1,
          type: 'products',
          filename: 'products_import_2024_01_15.xlsx',
          records: 150,
          success: 148,
          failed: 2,
          created_at: '2024-01-15 14:30:00',
          status: 'completed'
        },
        {
          id: 2,
          type: 'customers',
          filename: 'customers_import_2024_01_14.xlsx',
          records: 50,
          success: 50,
          failed: 0,
          created_at: '2024-01-14 11:15:00',
          status: 'completed'
        },
        {
          id: 3,
          type: 'suppliers',
          filename: 'suppliers_import_2024_01_13.xlsx',
          records: 25,
          success: 23,
          failed: 2,
          created_at: '2024-01-13 16:45:00',
          status: 'completed'
        }
      ]);
    }
  };

  const handleExport = async (type) => {
    if (!canExport) {
      toast.error('You do not have permission to export data');
      return;
    }

    try {
      setIsLoading(true);
      
      // Create a temporary link to download the file
      const token = localStorage.getItem('auth_token');
      const url = `${apiService.baseURL}/system/export/${type}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      // Get the filename from the response headers or create a default one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${type}_export_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success(`${type} data exported successfully`);
      fetchExportData();
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error(`Failed to export ${type} data: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please select a valid Excel file (.xlsx or .xls)');
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    // Reset the file input
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleImport = async (type) => {
    if (!canImport) {
      toast.error('You do not have permission to import data');
      return;
    }

    if (!selectedFile) {
      toast.error('Please select a file to import');
      return;
    }

    try {
      setIsLoading(true);
      setIsUploading(true);
      setUploadProgress(0);

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('type', type);
      formData.append('settings', JSON.stringify(importSettings));

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 10;
        });
      }, 200);

      const response = await apiService.post('/system/import', formData);
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.success) {
        toast.success(`${type} data imported successfully`);
        setSelectedFile(null);
        fetchImportHistory();
      } else {
        throw new Error(response.message || 'Import failed');
      }
    } catch (error) {
      console.error('Error importing data:', error);
      toast.error(`Failed to import ${type} data: ${error.message}`);
    } finally {
      setIsLoading(false);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'in_progress':
        return <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'products':
        return <Package className="w-5 h-5 text-blue-500" />;
      case 'customers':
        return <Users className="w-5 h-5 text-green-500" />;
      case 'suppliers':
        return <Truck className="w-5 h-5 text-orange-500" />;
      default:
        return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'products':
        return 'Products';
      case 'customers':
        return 'Customers';
      case 'suppliers':
        return 'Suppliers';
      default:
        return type;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const filteredImportHistory = importHistory.filter(item => {
    const matchesSearch = item.filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || item.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getTotalRecords = () => {
    const productsCount = Number(exportData.products.count) || 0;
    const customersCount = Number(exportData.customers.count) || 0;
    const suppliersCount = Number(exportData.suppliers.count) || 0;
    return productsCount + customersCount + suppliersCount;
  };

  const getTotalSize = () => {
    const productsSize = Number(exportData.products.size) || 0;
    const customersSize = Number(exportData.customers.size) || 0;
    const suppliersSize = Number(exportData.suppliers.size) || 0;
    const total = productsSize + customersSize + suppliersSize;
    
    
    return total;
  };

  if (!canExport && !canImport && !canView) {
    return (
      <Layout>
        <div className="container mx-auto p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield size={32} className="text-yellow-600" />
            </div>
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">Access Denied</h3>
            <p className="text-yellow-700">You don't have permission to access data management features.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Page Header */}
        <div className="bg-white rounded-2xl mt-20 shadow-xl border border-gray-200 p-8 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Database className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
                  Data Management
                </h1>
                <p className="text-gray-600 text-lg">Import, export, and manage your business data with ease</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-6 py-3 rounded-xl flex items-center shadow-lg">
                <Database className="w-5 h-5 mr-2" />
                <span className="font-medium">{getTotalRecords().toLocaleString()} Records</span>
              </div>
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-xl flex items-center shadow-lg">
                <HardDrive className="w-5 h-5 mr-2" />
                <span className="font-medium">{isNaN(getTotalSize()) ? '0.0' : getTotalSize().toFixed(1)} MB</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <Settings className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Data Operations</h2>
              <p className="text-gray-600">Choose your data management operation</p>
            </div>
          </div>
          <nav className="flex space-x-2">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`flex items-center px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
                    activeTab === 'overview'
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Overview
                </button>
                {canExport && (
                  <button
                    onClick={() => setActiveTab('export')}
                    className={`flex items-center px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
                      activeTab === 'export'
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export Data
                  </button>
                )}
                {canImport && (
                  <button
                    onClick={() => setActiveTab('import')}
                    className={`flex items-center px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
                      activeTab === 'import'
                        ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Import Data
                  </button>
                )}
                {canView && (
                  <button
                    onClick={() => setActiveTab('history')}
                    className={`flex items-center px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
                      activeTab === 'history'
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Import History
                  </button>
                )}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Dashboard Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-600 mb-1">Total Records</p>
                    <p className="text-3xl font-bold text-blue-900">{getTotalRecords().toLocaleString()}</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                    <Database className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-600 mb-1">Storage Used</p>
                    <p className="text-3xl font-bold text-green-900">{isNaN(getTotalSize()) ? '0.0' : getTotalSize().toFixed(1)} MB</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                    <HardDrive className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-600 mb-1">Import Operations</p>
                    <p className="text-3xl font-bold text-purple-900">{importHistory.length}</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                    <Upload className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-orange-600 mb-1">Success Rate</p>
                    <p className="text-3xl font-bold text-orange-900">
                      {importHistory.length > 0 
                        ? Math.round((importHistory.filter(h => h.status === 'completed').length / importHistory.length) * 100)
                        : 100}%
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                    <CheckCircle className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>
            </div>

            {/* Data Types Overview */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Package className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="ml-4">
                        <h3 className="font-semibold text-gray-900">Products</h3>
                        <p className="text-sm text-gray-500">{exportData.products.count.toLocaleString()} records</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">{isNaN(exportData.products.size) ? '0.0' : exportData.products.size} MB</p>
                      <p className="text-xs text-gray-500">Size</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Last Export</span>
                      <span className="text-gray-900">
                        {exportData.products.lastExport ? formatDate(exportData.products.lastExport) : 'Never'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min((exportData.products.count / 2000) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>

              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-green-600" />
                    </div>
                      <div className="ml-4">
                        <h3 className="font-semibold text-gray-900">Customers</h3>
                        <p className="text-sm text-gray-500">{exportData.customers.count.toLocaleString()} records</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">{isNaN(exportData.customers.size) ? '0.0' : exportData.customers.size} MB</p>
                      <p className="text-xs text-gray-500">Size</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Last Export</span>
                      <span className="text-gray-900">
                        {exportData.customers.lastExport ? formatDate(exportData.customers.lastExport) : 'Never'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min((exportData.customers.count / 500) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>

              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Truck className="w-6 h-6 text-orange-600" />
                    </div>
                      <div className="ml-4">
                        <h3 className="font-semibold text-gray-900">Suppliers</h3>
                        <p className="text-sm text-gray-500">{exportData.suppliers.count.toLocaleString()} records</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">{isNaN(exportData.suppliers.size) ? '0.0' : exportData.suppliers.size} MB</p>
                      <p className="text-xs text-gray-500">Size</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Last Export</span>
                      <span className="text-gray-900">
                        {exportData.suppliers.lastExport ? formatDate(exportData.suppliers.lastExport) : 'Never'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min((exportData.suppliers.count / 100) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {canExport && (
                    <button
                      onClick={() => setActiveTab('export')}
                      className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 group"
                    >
                      <Download className="w-6 h-6 text-gray-400 group-hover:text-blue-500 mr-3" />
                      <span className="text-gray-600 group-hover:text-blue-600 font-medium">Export Data</span>
                    </button>
                  )}
                  {canImport && (
                    <button
                      onClick={() => setActiveTab('import')}
                      className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all duration-200 group"
                    >
                      <Upload className="w-6 h-6 text-gray-400 group-hover:text-green-500 mr-3" />
                      <span className="text-gray-600 group-hover:text-green-600 font-medium">Import Data</span>
                    </button>
                  )}
                  {canView && (
                    <button
                      onClick={() => setActiveTab('history')}
                      className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all duration-200 group"
                    >
                      <FileText className="w-6 h-6 text-gray-400 group-hover:text-purple-500 mr-3" />
                      <span className="text-gray-600 group-hover:text-purple-600 font-medium">View History</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

        {activeTab === 'export' && canExport && (
          <div className="space-y-8">
            {/* Export Section */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Export Data to Excel</h2>
                    <p className="text-gray-600">Export your data to Excel files for backup, analysis, or migration purposes.</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                      <CheckCircle className="w-4 h-4 inline mr-1" />
                      Ready to Export
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Products Export */}
                  <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                          <Package className="w-6 h-6 text-white" />
                        </div>
                        <div className="ml-4">
                          <h3 className="font-semibold text-gray-900">Products</h3>
                          <p className="text-sm text-gray-600">{exportData.products.count.toLocaleString()} records</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">{exportData.products.size} MB</p>
                        <p className="text-xs text-gray-500">Size</p>
                      </div>
                    </div>
                    <div className="mb-4">
                      <p className="text-sm text-gray-600">
                        Last export: {exportData.products.lastExport ? formatDate(exportData.products.lastExport) : 'Never'}
                      </p>
                    </div>
                    <button
                      onClick={() => handleExport('products')}
                      disabled={isLoading}
                      className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-3 rounded-lg hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium transition-all duration-200"
                    >
                      {isLoading ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Export Products
                    </button>
                  </div>

                  {/* Customers Export */}
                  <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                          <Users className="w-6 h-6 text-white" />
                        </div>
                        <div className="ml-4">
                          <h3 className="font-semibold text-gray-900">Customers</h3>
                          <p className="text-sm text-gray-600">{exportData.customers.count.toLocaleString()} records</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">{exportData.customers.size} MB</p>
                        <p className="text-xs text-gray-500">Size</p>
                      </div>
                    </div>
                    <div className="mb-4">
                      <p className="text-sm text-gray-600">
                        Last export: {exportData.customers.lastExport ? formatDate(exportData.customers.lastExport) : 'Never'}
                      </p>
                    </div>
                    <button
                      onClick={() => handleExport('customers')}
                      disabled={isLoading}
                      className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-3 rounded-lg hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium transition-all duration-200"
                    >
                      {isLoading ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Export Customers
                    </button>
                  </div>

                  {/* Suppliers Export */}
                  <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                          <Truck className="w-6 h-6 text-white" />
                        </div>
                        <div className="ml-4">
                          <h3 className="font-semibold text-gray-900">Suppliers</h3>
                          <p className="text-sm text-gray-600">{exportData.suppliers.count.toLocaleString()} records</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">{exportData.suppliers.size} MB</p>
                        <p className="text-xs text-gray-500">Size</p>
                      </div>
                    </div>
                    <div className="mb-4">
                      <p className="text-sm text-gray-600">
                        Last export: {exportData.suppliers.lastExport ? formatDate(exportData.suppliers.lastExport) : 'Never'}
                      </p>
                    </div>
                    <button
                      onClick={() => handleExport('suppliers')}
                      disabled={isLoading}
                      className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-3 rounded-lg hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium transition-all duration-200"
                    >
                      {isLoading ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Export Suppliers
                    </button>
                  </div>
                </div>

                <div className="mt-8 bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                      <Info className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-2">Export Information</h4>
                      <p className="text-blue-800 text-sm leading-relaxed">
                        Exported files will include all current data with proper formatting. Large datasets may take several minutes to export. 
                        Files are saved in Excel format (.xlsx) and can be opened in any spreadsheet application.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

        {activeTab === 'import' && canImport && (
          <div className="space-y-8">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Import Data from Excel</h2>
                    <p className="text-gray-600">Import data from Excel files. Make sure your file follows the required format.</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
                      <Upload className="w-4 h-4 inline mr-1" />
                      Ready to Import
                    </div>
                  </div>
                </div>

                {/* File Selection */}
                <div className="mb-8">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Select Excel File
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileSelect}
                      className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400 transition-colors duration-200"
                    />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="text-center">
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Click to select or drag and drop</p>
                        <p className="text-xs text-gray-400">Supports .xlsx and .xls files</p>
                      </div>
                    </div>
                  </div>
                  {selectedFile && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <FileCheck className="w-5 h-5 text-green-600 mr-3" />
                          <div>
                            <p className="text-sm font-medium text-green-900">File Selected</p>
                            <p className="text-sm text-green-700">
                              {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={handleRemoveFile}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors duration-200"
                          title="Remove file"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Import Settings */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">Import Settings</h3>
                  <div className="bg-gray-50 rounded-xl p-6 space-y-6">
                    <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                      <div>
                        <h4 className="font-semibold text-gray-900">Update Existing Records</h4>
                        <p className="text-sm text-gray-500">Update existing records if they match</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={importSettings.updateExisting}
                          onChange={(e) => setImportSettings({
                            ...importSettings,
                            updateExisting: e.target.checked
                          })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                      <div>
                        <h4 className="font-semibold text-gray-900">Skip Duplicates</h4>
                        <p className="text-sm text-gray-500">Skip records that already exist</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={importSettings.skipDuplicates}
                          onChange={(e) => setImportSettings({
                            ...importSettings,
                            skipDuplicates: e.target.checked
                          })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                      <div>
                        <h4 className="font-semibold text-gray-900">Validate Data</h4>
                        <p className="text-sm text-gray-500">Validate data before importing</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={importSettings.validateData}
                          onChange={(e) => setImportSettings({
                            ...importSettings,
                            validateData: e.target.checked
                          })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Import Buttons */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <button
                    onClick={() => handleImport('products')}
                    disabled={isLoading || !selectedFile}
                    className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-4 rounded-xl hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    {isLoading ? (
                      <RefreshCw className="w-5 h-5 mr-3 animate-spin" />
                    ) : (
                      <Package className="w-5 h-5 mr-3" />
                    )}
                    Import Products
                  </button>

                  <button
                    onClick={() => handleImport('customers')}
                    disabled={isLoading || !selectedFile}
                    className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4 rounded-xl hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    {isLoading ? (
                      <RefreshCw className="w-5 h-5 mr-3 animate-spin" />
                    ) : (
                      <Users className="w-5 h-5 mr-3" />
                    )}
                    Import Customers
                  </button>

                  <button
                    onClick={() => handleImport('suppliers')}
                    disabled={isLoading || !selectedFile}
                    className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-4 rounded-xl hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    {isLoading ? (
                      <RefreshCw className="w-5 h-5 mr-3 animate-spin" />
                    ) : (
                      <Truck className="w-5 h-5 mr-3" />
                    )}
                    Import Suppliers
                  </button>
                </div>

                {/* Upload Progress */}
                {isUploading && (
                  <div className="mt-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-blue-900">Uploading File</h4>
                        <span className="text-sm font-medium text-blue-700">{Math.round(uploadProgress)}%</span>
                      </div>
                      <div className="w-full bg-blue-200 rounded-full h-3">
                        <div 
                          className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-8 bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center mr-4">
                      <AlertTriangle className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-yellow-900 mb-3">Import Guidelines</h4>
                      <ul className="text-yellow-800 text-sm space-y-2">
                        <li className="flex items-start">
                          <span className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          Ensure your Excel file follows the required format
                        </li>
                        <li className="flex items-start">
                          <span className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          Required columns must be present and properly named
                        </li>
                        <li className="flex items-start">
                          <span className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          Data validation will be performed before import
                        </li>
                        <li className="flex items-start">
                          <span className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          Large files may take several minutes to process
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

        {activeTab === 'history' && canView && (
          <div className="space-y-8">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200">
                <div className="px-8 py-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Import History</h2>
                      <p className="text-gray-600 mt-1">Track all your data import operations</p>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="relative">
                        <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search imports..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>
                      <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="all">All Status</option>
                        <option value="completed">Completed</option>
                        <option value="failed">Failed</option>
                        <option value="in_progress">In Progress</option>
                      </select>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setViewMode('grid')}
                          className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-purple-100 text-purple-600' : 'text-gray-400 hover:text-gray-600'}`}
                        >
                          <Grid3X3 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setViewMode('list')}
                          className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-purple-100 text-purple-600' : 'text-gray-400 hover:text-gray-600'}`}
                        >
                          <List className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-8">
                  {viewMode === 'list' ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Type
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              File Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Records
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Success
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Failed
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Date
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {filteredImportHistory.map((import_) => (
                            <tr key={import_.id} className="hover:bg-gray-50 transition-colors duration-200">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  {getTypeIcon(import_.type)}
                                  <span className="ml-2 text-sm font-medium text-gray-900">
                                    {getTypeLabel(import_.type)}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {import_.filename}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {import_.records}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                                {import_.success}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                                {import_.failed}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  {getStatusIcon(import_.status)}
                                  <span className="ml-2 text-sm text-gray-900 capitalize">{import_.status}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatDate(import_.created_at)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {filteredImportHistory.map((import_) => (
                        <div key={import_.id} className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center">
                              {getTypeIcon(import_.type)}
                              <span className="ml-2 text-sm font-semibold text-gray-900">
                                {getTypeLabel(import_.type)}
                              </span>
                            </div>
                            {getStatusIcon(import_.status)}
                          </div>
                          <div className="space-y-3">
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">File Name</p>
                              <p className="text-sm text-gray-900 font-medium truncate">{import_.filename}</p>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                              <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide">Total</p>
                                <p className="text-sm font-semibold text-gray-900">{import_.records}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide">Success</p>
                                <p className="text-sm font-semibold text-green-600">{import_.success}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide">Failed</p>
                                <p className="text-sm font-semibold text-red-600">{import_.failed}</p>
                              </div>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Date</p>
                              <p className="text-sm text-gray-900">{formatDate(import_.created_at)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {filteredImportHistory.length === 0 && (
                    <div className="text-center py-12">
                      <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No import history found</h3>
                      <p className="text-gray-500">No imports match your current search and filter criteria.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
      </div>
    </Layout>
  );
};

export default DataManagement;
